import { useState } from "react";
import { Bell, Check, CheckCheck, Calendar, XCircle, Trash2, MessageSquare, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
} from "@/hooks/useNotifications";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const { data: notifications = [], isLoading } = useNotifications();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markAsRead = useMarkNotificationRead();
  const markAllAsRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();
  const { isSuperAdmin, isBusinessAdmin, isStaff } = useAuth();
  const navigate = useNavigate();

  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications = notifications.filter((n) => n.is_read);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "booking_confirmation":
        return <Calendar className="h-4 w-4 text-green-500" />;
      case "booking_cancellation":
        return <XCircle className="h-4 w-4 text-destructive" />;
      case "booking_reminder":
        return <Bell className="h-4 w-4 text-amber-500" />;
      case "ticket_response":
      case "new_ticket":
      case "new_support_ticket":
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case "leave_approved":
      case "shift_change_approved":
        return <Calendar className="h-4 w-4 text-green-500" />;
      case "leave_rejected":
      case "shift_change_rejected":
        return <Calendar className="h-4 w-4 text-destructive" />;
      case "leave_request":
      case "shift_change_request":
        return <Calendar className="h-4 w-4 text-amber-500" />;
      case "new_booking":
        return <Calendar className="h-4 w-4 text-green-500" />;
      case "gift_received":
        return <Bell className="h-4 w-4 text-green-500" />;
      case "new_business_signup":
        return <Bell className="h-4 w-4 text-blue-500" />;
      case "security_alert":
        return <Bell className="h-4 w-4 text-destructive" />;
      case "shift_swap_request":
        return <ArrowLeftRight className="h-4 w-4 text-amber-500" />;
      case "shift_swap_approved":
        return <ArrowLeftRight className="h-4 w-4 text-green-500" />;
      case "shift_swap_rejected":
        return <ArrowLeftRight className="h-4 w-4 text-destructive" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getNotificationRoute = (notification: { type: string; booking_id: string | null }) => {
    switch (notification.type) {
      case "booking_confirmation":
      case "booking_cancellation":
      case "booking_reminder":
        return "/bookings";
      case "ticket_response":
        if (isSuperAdmin) return "/admin/tickets";
        if (isBusinessAdmin || isStaff) return "/business/support";
        return "/business/support";
      case "new_ticket":
      case "new_support_ticket":
        if (isSuperAdmin) return "/admin/tickets";
        return "/business/support";
      case "leave_approved":
      case "leave_rejected":
      case "shift_change_approved":
      case "shift_change_rejected":
        return "/staff/availability";
      case "leave_request":
        if (isBusinessAdmin || isSuperAdmin) return "/business/leave-requests";
        return "/staff/availability";
      case "new_booking":
        if (isBusinessAdmin || isSuperAdmin) return "/business/calendar";
        if (isStaff) return "/staff/dashboard";
        return "/bookings";
      case "shift_change_request":
        if (isBusinessAdmin || isSuperAdmin) return "/business/leave-requests";
        return "/staff/availability";
      case "gift_received":
        return "/business/subscription";
      case "new_business_signup":
        if (isSuperAdmin) return "/admin/communities";
        return null;
      case "security_alert":
        if (isSuperAdmin) return "/admin/users";
        return null;
      case "shift_swap_request":
        if (isBusinessAdmin || isSuperAdmin) return "/business/shift-swaps";
        return "/staff/availability";
      case "shift_swap_approved":
      case "shift_swap_rejected":
        return "/staff/availability";
      default:
        return null;
    }
  };

  const handleNotificationClick = (notification: { id: string; is_read: boolean; type: string; booking_id: string | null }) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }
    
    const route = getNotificationRoute(notification);
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  const renderNotificationItem = (notification: typeof notifications[0]) => (
    <div
      key={notification.id}
      className={cn(
        "flex gap-3 p-3 transition-colors hover:bg-muted/50 cursor-pointer",
        !notification.is_read && "bg-primary/5"
      )}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="mt-0.5">
        {getNotificationIcon(notification.type)}
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-tight">
            {notification.title}
          </p>
          <div className="flex items-center gap-1">
            {!notification.is_read && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  markAsRead.mutate(notification.id);
                }}
                disabled={markAsRead.isPending}
              >
                <Check className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                deleteNotification.mutate(notification.id);
              }}
              disabled={deleteNotification.isPending}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground/70">
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
          })}
        </p>
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 p-0 z-[60]" align="end" sideOffset={8} collisionPadding={16}>
        <div className="flex items-center justify-between p-4 pb-2">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>
        <Separator />
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="mb-2 h-8 w-8" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div>
              {unreadNotifications.length > 0 && (
                <>
                  <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Unread
                  </p>
                  <div className="divide-y">
                    {unreadNotifications.map(renderNotificationItem)}
                  </div>
                </>
              )}
              {readNotifications.length > 0 && (
                <>
                  {unreadNotifications.length > 0 && <Separator />}
                  <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    Earlier
                  </p>
                  <div className="divide-y">
                    {readNotifications.map((n) => (
                      <div key={n.id} className="opacity-70">
                        {renderNotificationItem(n)}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </ScrollArea>
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => { setOpen(false); navigate(isSuperAdmin ? "/admin/tickets" : "/bookings"); }}
              >
                {isSuperAdmin ? "View all tickets" : "View all bookings"}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
